import { BigNumber } from 'bignumber.js';

/**
 * Minimum number of leading zeros to trigger zero-index notation
 */
export const MIN_LEADING_ZEROS = 5;

/**
 * Common result interface for number formatting functions
 */
export interface FormatResult {
  realNotation: string;
  zeroIndexNotation: ZeroIndexNotationParts | null;
}

/**
 * Converts an input into a BigNumber and returns common string parts.
 */
function getBigNumberParts(value: number | BigNumber | string): {
  bn: BigNumber;
  bnAbs: BigNumber;
  sign: string;
  intStr: string;
  fracStr: string; // Fractional part of the number i.e. everything after the decimal point.
  fixedStr: string;
} {
  const bn = BigNumber.isBigNumber(value) ? value : new BigNumber(value);
  const sign = bn.isNegative() ? '-' : '';
  const bnAbs = bn.abs();
  const fixedStr = bnAbs.toFixed(); // Always includes a decimal point if there's any fractional part
  const [intStr, fracStr = ''] = fixedStr.split('.');
  return { bn, bnAbs, sign, intStr, fracStr, fixedStr };
}

/**
 * Inserts thousand separators into a numeric string.
 * Safely handles decimal strings by only applying separators to the integer part.
 *
 * @example
 * addThousandSeparators('1234567') -> '1,234,567'
 * addThousandSeparators('-123456') -> '-123,456'
 * addThousandSeparators('1000') -> '1,000'
 * addThousandSeparators('1234.5678') -> '1,234.5678'
 * addThousandSeparators('0.000359') -> '0.000359'
 */
export function addThousandSeparators(numStr: string): string {
  // Handle any leading sign.
  let prefix = '';
  if (numStr[0] === '-' || numStr[0] === '+') {
    prefix = numStr[0];
    numStr = numStr.slice(1);
  }

  // Split on the decimal point so separators are only applied to the integer part.
  const dotIdx = numStr.indexOf('.');
  if (dotIdx !== -1) {
    const intPart = numStr.slice(0, dotIdx);
    const decPart = numStr.slice(dotIdx);
    return prefix + intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + decPart;
  }

  return prefix + numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a number as a whole number
 *
 * @example
 * formatAsWholeNumber(123456.789) -> '123,457'
 * formatAsWholeNumber(-123.789) -> '-124'
 * formatAsWholeNumber(1000.1) -> '1,000'
 */
export function formatAsWholeNumber(value: number | BigNumber): FormatResult {
  const bn = BigNumber.isBigNumber(value) ? value : new BigNumber(value);
  const rounded = bn.integerValue(BigNumber.ROUND_HALF_UP);
  return {
    realNotation: addThousandSeparators(rounded.toFixed(0)),
    zeroIndexNotation: null,
  };
}

/**
 * Formats a number to a specific number of significant figures using BigNumber.
 * Handles both standard numbers and very small decimals.
 *
 * Examples:
 *   formatSignificantFigures(123456, 3)     -> "123000"
 *   formatSignificantFigures(123.456, 3)     -> "123"
 *   formatSignificantFigures(123.456, 4)     -> "123.5"
 *   formatSignificantFigures(0.0012345, 3)   -> "0.00123"
 *   formatSignificantFigures(-1.25, 2)       -> "-1.3"
 */
export function formatSignificantFigures(
  value: number | BigNumber | string,
  sigDigits: number
): string {
  const { bn, sign, bnAbs } = getBigNumberParts(value);
  if (bn.isZero()) return '0';

  // BigNumber represents the number as: bnAbs = coefficient × 10^(e)
  // With a normalized coefficient having (e+1) digits before the decimal.
  // Examples:
  //  • 123.456 is represented with coefficient 1.23456 and e=2 (1.23456 × 10^2)
  //  • 0.00123 is represented with coefficient 1.23 and e=-3 (1.23 × 10^-3)
  //  • In both cases, e+1 tells us how many digits are before the decimal
  // Don't worry buddy, it just works ;)
  const exp = bnAbs.e || 0;
  const decimals = sigDigits - (exp + 1);

  let result: string;
  if (decimals >= 0) {
    result = bnAbs.toFixed(decimals, BigNumber.ROUND_HALF_UP);
  } else {
    const factor = new BigNumber(10).pow(-decimals);
    result = bnAbs
      .div(factor)
      .integerValue(BigNumber.ROUND_HALF_UP)
      .times(factor)
      .toFixed(0);
  }

  // Remove unnecessary trailing zeros (and a dangling decimal point).
  if (result.indexOf('.') !== -1) {
    result = result.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  return sign + result;
}

/**
 * Format a number with significant figures and check for zero-index notation
 */
export function formatWithSignificantFigures(
  value: number | BigNumber,
  significantFigures: number
): FormatResult {
  const bn = BigNumber.isBigNumber(value) ? value : new BigNumber(value);
  const realNotation = formatSignificantFigures(value, significantFigures);
  let zeroIndexNotation: ZeroIndexNotationParts | null = null;

  // 🔍 Check if we should use zero-index notation for small numbers
  if (bn.abs().lt(1)) {
    const candidate = formatZeroIndexNotation(value, significantFigures);
    if (candidate && candidate.truncatedZeroCount >= MIN_LEADING_ZEROS) {
      zeroIndexNotation = candidate;
    }
  }

  return { realNotation, zeroIndexNotation };
}

/**
 * Format a number with fixed decimal places
 *
 * @param value - The number to format
 * @param decimals - The number of decimal places to show (defaults to 2)
 * @param trimTrailingZeros - Whether to remove trailing zeros after the decimal (defaults to false)
 *
 * @example
 * formatWithFixedDecimals(123.456, 2, true) -> '123.45'
 * formatWithFixedDecimals(123.456, 2, false) -> '123.4560'
 * formatWithFixedDecimals(123.456, 0, true) -> '123'
 * formatWithFixedDecimals(123.4, 3, false) -> '123.400'
 */
export function formatWithFixedDecimals(
  value: number | BigNumber,
  decimals: number = 2,
  trimTrailingZeros: boolean = false
): FormatResult {
  const bn = BigNumber.isBigNumber(value) ? value : new BigNumber(value);

  // When decimals is 0, return a whole number
  if (decimals === 0) {
    return {
      realNotation: addThousandSeparators(
        bn.toFixed(0, BigNumber.ROUND_HALF_UP)
      ),
      zeroIndexNotation: null,
    };
  }

  // For all other cases, preserve the exact number of decimal places specified
  const fixed = bn.toFixed(decimals, BigNumber.ROUND_HALF_UP);

  if (fixed.indexOf('.') !== -1) {
    const [intPart, decPart] = fixed.split('.');

    // Handle trailing zeros
    if (trimTrailingZeros) {
      const trimmedDecimal = decPart.replace(/0+$/, '');

      // If all decimal digits were zeros, return just the integer part
      if (trimmedDecimal === '') {
        return {
          realNotation: addThousandSeparators(intPart),
          zeroIndexNotation: null,
        };
      }

      // Otherwise return with the trimmed decimal part
      return {
        realNotation: addThousandSeparators(intPart) + '.' + trimmedDecimal,
        zeroIndexNotation: null,
      };
    }

    // If not trimming, return with the full decimal part
    return {
      realNotation: addThousandSeparators(intPart) + '.' + decPart,
      zeroIndexNotation: null,
    };
  } else {
    return {
      realNotation: addThousandSeparators(fixed),
      zeroIndexNotation: null,
    };
  }
}

/**
 * Formats a number using Zero‑Index Notation.
 *
 * For numbers whose fractional part starts with one or more zeros,
 * this returns an object containing:
 *  - prefix: The integer part plus "0" (e.g. "0.0" or "123.0")
 *  - leadingZeros: The count of leading zeros (actual count)
 *  - truncatedZeroCount: The count of leading zeros (minus one)
 *  - significantDigits: The digits immediately following the leading zeros.
 *
 * If the number isn't a candidate (for example, its fractional part doesn't start with '0'),
 * this function returns null.
 *
 * @example
 *   formatZeroIndexNotation(0.000000789, 3)
 *      -> { prefix: "0.0", leadingZeros: 6, truncatedZeroCount: 5, significantDigits: "789" }
 *   formatZeroIndexNotation(123.0000000000009, 3)
 *      -> { prefix: "123.0", leadingZeros: 12, truncatedZeroCount: 11, significantDigits: "900" }
 */
export type ZeroIndexNotationParts = {
  prefix: string;
  leadingZeros: number; // Actual count of leading zeros
  truncatedZeroCount: number; // Derived count (leadingZeros - 1), used for display
  significantDigits: string;
};

export function formatZeroIndexNotation(
  value: number | BigNumber,
  sigDigits = 1
): ZeroIndexNotationParts | null {
  const { bn, sign, intStr, fracStr } = getBigNumberParts(value);
  if (bn.isZero()) return null;

  // Only consider numbers whose fractional part begins with zero.
  if (!fracStr || fracStr[0] !== '0') return null;

  // Count how many zeros start the fractional part.
  const leadingZeros = (fracStr.match(/^0+/) || [''])[0].length;

  // Define truncatedZeroCount as (leading zeros - 1)
  const truncatedZeroCount = Math.max(leadingZeros - 1, 0);

  // Extract the significant digits following the leading zeros.
  let significantDigits = fracStr.slice(leadingZeros, leadingZeros + sigDigits);
  significantDigits = significantDigits.padEnd(sigDigits, '0');

  return {
    prefix: sign + intStr + '.0',
    leadingZeros,
    truncatedZeroCount,
    significantDigits,
  };
}

/**
 * Apply the appropriate formatting method based on the provided options
 */
export function formatNumberByOptions(
  value: number | BigNumber,
  options: {
    roundToWholeNumber?: boolean;
    decimals?: number;
    significantFigures?: number;
    trimTrailingZeros?: boolean;
  }
): FormatResult {
  const {
    roundToWholeNumber,
    decimals,
    significantFigures,
    trimTrailingZeros,
  } = options;

  // Format selection logic, in order of precedence:
  // 1. Whole numbers (if requested)
  // 2. Significant figures (if specified)
  // 3. Fixed decimals (if specified)
  // 4. Smart default formatting

  if (roundToWholeNumber) {
    return formatAsWholeNumber(value);
  } else if (typeof significantFigures === 'number') {
    return formatWithSignificantFigures(value, significantFigures);
  } else if (typeof decimals === 'number') {
    return formatWithFixedDecimals(value, decimals, trimTrailingZeros);
  } else {
    // Default to smart price formatting
    return formatPriceValues(value);
  }
}

/**
 * Formats a price value for display.
 *
 * Requirements:
 *  - For numbers ≥ 1: Show thousand separators and round to 2 decimals (if fractional).
 *  - For numbers < 1:
 *      • If the fractional part does NOT begin with multiple zeros, round to 2 decimals.
 *      • If the fractional part begins with several zeros (we use a threshold of 3 leading zeros),
 *        then use zero‑index notation.
 *  - In all cases, ensure at least 2 decimals are shown (except for integer values ≥ 1).
 *  - Negative numbers are supported.
 *
 * Returns an object with:
 *  - realNotation: The formatted number string.
 *  - zeroIndexNotation: Either null or the parts object returned by formatZeroIndexNotation.
 */
export function formatPriceValues(
  value: number | BigNumber | string
): FormatResult {
  const PRICE_SIG_DIGITS = 3;

  const { bn, sign, intStr, fracStr } = getBigNumberParts(value);
  const zeroIndexNotation = formatZeroIndexNotation(bn, PRICE_SIG_DIGITS);

  let realNotation: string;

  if (bn.abs().gte(1)) {
    // For values ≥ 1, if the number is an integer, simply add thousand separators.
    if (bn.isInteger()) {
      // Use sign + intStr to preserve the negative sign for integer values
      realNotation = addThousandSeparators(sign + intStr);
    } else {
      // Round to 2 decimal places.
      const fixed = bn.toFixed(2, BigNumber.ROUND_HALF_UP);
      const [intPart, decPart = ''] = fixed.split('.');

      // Ensure at least 2 decimal digits.
      const decimals = decPart.padEnd(2, '0');
      realNotation = addThousandSeparators(intPart) + '.' + decimals;
    }
  } else {
    // For values < 1:
    if (fracStr[0] !== '0') {
      // If the first fractional digit is nonzero, simply round to 2 decimals.
      realNotation = bn.toFixed(2, BigNumber.ROUND_HALF_UP);
    } else {
      // Count how many zeros start the fractional part.
      const leadingZeros = (fracStr.match(/^0+/) || [''])[0].length;

      // Use zero‑index formatting only if there are at least MIN_LEADING_ZEROS leading zeros.
      if (leadingZeros >= MIN_LEADING_ZEROS && zeroIndexNotation !== null) {
        realNotation =
          zeroIndexNotation.prefix +
          '[' +
          zeroIndexNotation.truncatedZeroCount +
          ']' +
          zeroIndexNotation.significantDigits;
      } else {
        // Otherwise, use our significant-figure formatter to preserve the detail.
        realNotation = formatSignificantFigures(value, PRICE_SIG_DIGITS);

        // Ensure there are at least 2 decimals.
        if (realNotation.indexOf('.') === -1) {
          realNotation += '.00'; // This is due to price formatting requirements.
        } else {
          // Pad the decimal part to ensure at least 2 decimals.
          const parts = realNotation.split('.');
          if (parts[1].length < 2) {
            parts[1] = parts[1].padEnd(2, '0');
          }
          realNotation = parts.join('.');
        }
      }
    }
  }

  return {
    realNotation,
    zeroIndexNotation,
  };
}

/**
 * Determines if zero-index notation should be used based on the number and formatting result
 */
export function shouldUseZeroIndexNotation(result: FormatResult): boolean {
  // Check based on the actual leading zero count
  return Boolean(
    result.zeroIndexNotation &&
    result.zeroIndexNotation.leadingZeros >= MIN_LEADING_ZEROS
  );
}

/**
 * Normalizes spacing for suffix strings by automatically adding a leading space
 * when appropriate.
 *
 * Auto-spacing is applied when:
 * - Suffix starts with an alphanumeric character (letter or number)
 *
 * No space is added when:
 * - Suffix is empty or falsy (after trimming)
 * - Suffix starts with a symbol (%, !, etc.)
 *
 * @param suffix - The suffix string to normalize
 * @returns The normalized suffix with appropriate spacing
 *
 * @example
 * normalizeSuffix('ETH') -> ' ETH'
 * normalizeSuffix('crates') -> ' crates'
 * normalizeSuffix('%') -> '%'
 * normalizeSuffix(' ETH') -> ' ETH'
 * normalizeSuffix('') -> ''
 */
export function normalizeSuffix(suffix: string | undefined): string {
  suffix = suffix?.trim();
  if (!suffix) return '';

  // Add space before alphanumeric, but not symbols/punctuation
  if (/^[a-zA-Z0-9]/.test(suffix)) return ` ${suffix}`;

  return suffix;
}
